import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { Typography } from "@mui/material";
import { Tooltip } from "@avenue-8/sidekick-ui";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { FormattedDate } from "@shared/components/FormattedDate";
import {
  ContactName,
  ScoreBadge,
  ScoreBadgeText,
  SourceIconWrapper,
  ViewContactHint,
} from "@modules/radar/shared/components/SignalCardPrimitives.styles";

const ROTATION_INTERVAL_MS = 4000;
const TRANSITION_MS = 300;

type FakeSource = "instagram" | "facebook" | "web_search";

const SOURCE_ICONS: Record<FakeSource, React.ReactNode> = {
  instagram: <InstagramIcon fontSize="inherit" />,
  facebook: <FacebookIcon fontSize="inherit" />,
  web_search: <TravelExploreIcon fontSize="inherit" />,
};

const SOURCE_LABELS: Record<FakeSource, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  web_search: "Web Search",
};

const daysAgo = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
};

const MOMENTS = [
  {
    source: "instagram" as FakeSource,
    contactName: "Sarah Chen",
    score: 82,
    headline: "Announced a new role at a growing fintech startup",
    insight:
      "A quick congrats note keeps you top of mind before the new-job dust settles.",
    occurredAt: daysAgo(0),
  },
  {
    source: "facebook" as FakeSource,
    contactName: "James Whitfield",
    score: 88,
    headline: "Shared that his family is relocating across the state",
    insight:
      "Relocations often mean a home sale or purchase soon — a well-timed market note could open the door before he works with someone else.",
    occurredAt: daysAgo(1),
  },
  {
    source: "web_search" as FakeSource,
    contactName: "Elena Ruiz",
    score: 91,
    headline: "Company mentioned in a regional expansion announcement",
    insight:
      "Company news like this is a natural reason to reach out and see how things are going.",
    occurredAt: daysAgo(3),
  },
];

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(12px);
  }
`;

const Card = styled.div<{ $isExiting: boolean }>`
  position: absolute;
  left: 1rem;
  right: 1rem;
  bottom: 1rem;
  max-width: 330px;
  margin: 0 auto;
  padding: 1rem 1.25rem;
  border-radius: 1rem;
  background-color: ${(p) => p.theme.colors.background};
  border: 1px solid ${(p) => p.theme.colors.border2};
  box-shadow: ${(p) => p.theme.shadows.primary};
  animation: ${(p) => (p.$isExiting ? slideDown : slideUp)} ${TRANSITION_MS}ms
    ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  margin-bottom: 0.625rem;
`;

const Headline = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 0.25rem;
  line-height: 1.3;
`;

const Insight = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BottomRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  margin-top: 0.75rem;
`;

const TimeText = styled(Typography)`
  color: ${(p) => p.theme.text.colors.secondary};
  white-space: nowrap;
`;

export const RotatingSignalCard = () => {
  const [index, setIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, ROTATION_INTERVAL_MS - TRANSITION_MS);

    const advanceTimer = setTimeout(() => {
      setIndex((prev) => (prev + 1) % MOMENTS.length);
      setIsExiting(false);
    }, ROTATION_INTERVAL_MS);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(advanceTimer);
    };
  }, [index]);

  const moment = MOMENTS[index];

  return (
    <Card $isExiting={isExiting}>
      <TopRow>
        <Tooltip title={SOURCE_LABELS[moment.source]}>
          <SourceIconWrapper>{SOURCE_ICONS[moment.source]}</SourceIconWrapper>
        </Tooltip>
        <ContactName variant="subtitle2">{moment.contactName}</ContactName>
        <Tooltip title="Relevance score">
          <ScoreBadge $compact>
            <ScoreBadgeText data-compact variant="caption">
              {moment.score}
            </ScoreBadgeText>
          </ScoreBadge>
        </Tooltip>
      </TopRow>
      <Headline variant="subtitle2">{moment.headline}</Headline>
      <Insight variant="body2" color="text.secondary">
        {moment.insight}
      </Insight>
      <BottomRow>
        <TimeText variant="caption">
          <FormattedDate value={moment.occurredAt} relative placement="top" />
        </TimeText>
        <ViewContactHint variant="body2">
          Suggested approach
          <ArrowForwardIcon fontSize="inherit" />
        </ViewContactHint>
      </BottomRow>
    </Card>
  );
};
